import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import SupportSidebar from './SupportSidebar';
import Breadcrumb from './Breadcrumb';
import LogoutConfirmationModal from './common/LogoutConfirmationModal';
import authService from '../utils/authService';
import config from '../config';

const SupportLayout = () => {
  // Initialize sidebar state from localStorage or default to true for desktop
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    const saved = localStorage.getItem('supportSidebarOpen');
    return saved !== null ? JSON.parse(saved) : window.innerWidth >= 1024;
  });
  const [supportDetails, setSupportDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const navigate = useNavigate();
  const user = authService.getUser();

  // Save sidebar state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('supportSidebarOpen', JSON.stringify(sidebarOpen));
  }, [sidebarOpen]);

  // Fetch support details on component mount
  useEffect(() => {
    fetchSupportDetails();
  }, []);

  const fetchSupportDetails = async () => {
    try {
      const response = await fetch(`${config.API_BASE_URL}/api/support/dashboard/searchhotel`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader()
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.supportDetails) {
          setSupportDetails(data.supportDetails);
        }
      }
    } catch (error) {
      console.error('Error fetching support details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const handleLogoutConfirm = async () => {
    setShowLogoutModal(false);
    try {
      // Call logout API to clear session
      await fetch(`${config.API_BASE_URL}/api/auth/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeader()
        },
        credentials: 'include'
      });
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      // Clear local auth data and redirect
      authService.logout();
      navigate('/login');
    }
  };

  const handleLogoutCancel = () => {
    setShowLogoutModal(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-green-50">
      {/* Header */}
      <header className={`bg-white shadow-lg border-b border-gray-200 transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-16'}`}>
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg flex items-center justify-center mr-3">
                <i className="fas fa-headset text-white text-lg"></i>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">HotelApp</h1>
                <p className="text-sm text-gray-600">Support Panel</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-6">
            {/* Dynamic Support Details */}
            {loading ? (
              <div className="flex items-center space-x-4">
                <div className="animate-pulse bg-gray-200 h-12 w-24 rounded-lg"></div>
                <div className="animate-pulse bg-gray-200 h-12 w-20 rounded-lg"></div>
              </div>
            ) : supportDetails ? (
              <>
                <div className="text-right bg-red-50 px-4 py-2 rounded-lg border border-red-200">
                  <p className="text-xs text-red-600 font-medium">Open Tickets</p>
                  <p className="text-lg font-bold text-red-700">
                    {supportDetails.openTickets || '0'}
                  </p>
                </div>
                <div className="text-right bg-green-50 px-4 py-2 rounded-lg border border-green-200">
                  <p className="text-xs text-green-600 font-medium">Resolved Today</p>
                  <p className="text-lg font-bold text-green-700">
                    {supportDetails.resolvedToday || '0'}
                  </p>
                </div>
              </>
            ) : (
              <>
                <div className="text-right bg-gray-50 px-4 py-2 rounded-lg border border-gray-200">
                  <p className="text-xs text-gray-500 font-medium">Open Tickets</p>
                  <p className="text-lg font-bold text-gray-600">0</p>
                </div>
                <div className="text-right bg-gray-50 px-4 py-2 rounded-lg border border-gray-200">
                  <p className="text-xs text-gray-500 font-medium">Resolved Today</p>
                  <p className="text-lg font-bold text-gray-600">0</p>
                </div>
              </>
            )}
            
            <button
              onClick={handleLogoutClick}
              className="p-2 rounded-md text-gray-600 hover:text-red-600 hover:bg-red-50 transition-colors duration-200"
              title="Logout"
            >
              <i className="fas fa-sign-out-alt text-xl"></i>
            </button>
          </div>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className={`transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-16'}`}>
        <Breadcrumb />
      </div>

      <div className="flex">
        <SupportSidebar 
          user={user} 
          sidebarOpen={sidebarOpen} 
          setSidebarOpen={setSidebarOpen}
        />

        {/* Main Content */}
        <main className={`flex-1 transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-16'}`}>
          <Outlet />
        </main>
      </div>

      {/* Logout Confirmation Modal */}
      <LogoutConfirmationModal
        isOpen={showLogoutModal}
        onConfirm={handleLogoutConfirm}
        onCancel={handleLogoutCancel}
      />
    </div>
  );
};

export default SupportLayout;